/**
 * <ec-cookie-banner>
 *
 * Informational toast. Reveals itself on first visit (no stored ack) and
 * persists the acknowledgement in localStorage under `data-storage-key`.
 * Versioned key (e.g. `ec:cookie-banner:v1`) lets us re-prompt later if
 * the privacy policy materially changes.
 *
 * No consent gating: only cookies in use are strictly necessary + anonymous
 * audience measurement, both CNIL-exempt. The banner is purely transparent
 * disclosure, not a permission gate.
 */

const STORAGE_VALUE = 'acknowledged';

class EcCookieBanner extends HTMLElement {
  #onClick = null;

  connectedCallback() {
    if (this.#read() === STORAGE_VALUE) {
      // Already acknowledged — keep banner hidden.
      return;
    }

    // Reveal: remove [hidden], then add .is-visible on the next frame so
    // the transition fires from the initial transform/opacity.
    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
    });

    this.#onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const action = target?.closest('[data-cookie-action]')?.getAttribute('data-cookie-action');
      if (action === 'acknowledge') {
        this.#persist();
      }
    };
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    if (this.#onClick) {
      this.removeEventListener('click', this.#onClick);
      this.#onClick = null;
    }
  }

  #read() {
    try {
      return window.localStorage.getItem(this.#key());
    } catch {
      // Private mode or storage disabled — treat as "not yet acknowledged".
      return null;
    }
  }

  #persist() {
    try {
      window.localStorage.setItem(this.#key(), STORAGE_VALUE);
    } catch {
      // Silently degrade if storage is blocked.
    }
    this.#hide();
  }

  #key() {
    return this.dataset.storageKey || 'ec:cookie-banner:v1';
  }

  #hide() {
    this.classList.remove('is-visible');
    // Wait for the transition end before re-applying [hidden], otherwise
    // display:none kills the animation.
    const onEnd = () => {
      this.hidden = true;
      this.removeEventListener('transitionend', onEnd);
    };
    this.addEventListener('transitionend', onEnd);
    // Fallback if no transition fires (e.g. reduced motion shortcut).
    setTimeout(onEnd, 400);
  }
}

if (!customElements.get('ec-cookie-banner')) {
  customElements.define('ec-cookie-banner', EcCookieBanner);
}
